import { authClient } from "@/lib/auth-client";
import {
  formatComp,
  formatExperience,
  formatRelativeTime,
  getCountryFlag,
  getCountryName,
} from "@/utils/format";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Link } from "@tanstack/react-router";
import {
  Bookmark,
  Briefcase,
  Building2,
  Clock,
  DollarSign,
  MapPin,
  Wand2,
  Zap,
} from "lucide-react";

export interface JobCardProps {
  id: string;
  slug: string;
  title: string;
  company: string | null;
  seniority: string | null;
  yearsMin?: number | null;
  yearsMax?: number | null;
  workMode: string | null;
  locationPrimary: string | null;
  country: string | null;
  compMin: number | null;
  compMax: number | null;
  compCurrency: string | null;
  compPeriod: string | null;
  postedAt: Date | string | null;
  oneLiner: string | null;
  topSkills: string[];
  isSaved?: boolean;
  onToggleSave?: (e: React.MouseEvent) => void;
  minimal?: boolean;
}

export function JobCard({
  slug,
  title,
  company,
  seniority,
  yearsMin,
  yearsMax,
  workMode,
  locationPrimary,
  country,
  compMin,
  compMax,
  compCurrency,
  compPeriod,
  postedAt,
  oneLiner,
  topSkills,
  isSaved,
  onToggleSave,
  minimal = false,
}: JobCardProps) {
  const { data: session } = authClient.useSession();
  const compString = formatComp(compMin, compMax, compCurrency, compPeriod);
  const expString = formatExperience(yearsMin, yearsMax);
  const timeAgo = formatRelativeTime(postedAt);

  return (
    <div
      className={`group relative flex flex-col justify-between rounded-xl border border-border bg-card/60 shadow-sm transition duration-200 hover:border-brand/40 hover:bg-card hover:shadow-sm cursor-pointer ${
        minimal ? "p-5 gap-3.5" : "p-5 gap-3 sm:flex-row sm:items-center"
      }`}
    >
      {/* Main Details */}
      <div className="flex flex-col gap-2.5 min-w-0 flex-1">
        {/* Job Title, Action Icon Buttons & Company */}
        <div className="flex gap-2 items-start sm:items-center justify-between">
          <div className="w-full flex items-start justify-between gap-2 min-w-0 flex-wrap sm:flex-nowrap">
            <Link
              to="/jobs/$slug"
              params={{ slug }}
              className={`font-semibold text-foreground transition-colors group-hover:text-brand-foreground after:absolute after:inset-0 after:rounded-xl after:content-[''] ${
                minimal ? "text-base sm:text-lg leading-snug line-clamp-1" : "text-base sm:text-lg"
              }`}
            >
              {title}
            </Link>

            <div className="ml-auto flex items-center gap-2 shrink-0">
              {session?.user && (
                <>
                  <Link
                    to="/resume-tailor"
                    search={{ job: slug }}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    title="Tailor Resume for this job"
                    aria-label="Tailor Resume"
                    className="relative z-10 flex size-7 items-center justify-center rounded-lg border border-brand/30 text-brand-foreground hover:bg-primary/90 hover:text-white hover:border-brand transition cursor-pointer shadow-sm"
                  >
                    <Wand2 className="size-3.5" />
                  </Link>
                  <Link
                    to="/ats-booster"
                    search={{ job: slug }}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    title="Boost PDF with ATS keywords"
                    aria-label="Boost ATS"
                    className="flex size-7 items-center justify-center rounded-lg border border-emerald-500/30 text-emerald-500 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition cursor-pointer shadow-sm"
                  >
                    <Zap className="size-3.5" />
                  </Link>
                </>
              )}
              {!minimal && onToggleSave && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleSave(e);
                  }}
                  aria-label={isSaved ? "Remove from saved jobs" : "Save job"}
                  title={isSaved ? "Saved to your bookmarks" : "Save job"}
                  className={`relative z-10 flex size-7 items-center justify-center rounded-lg border transition cursor-pointer ${
                    isSaved
                      ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                      : "border-border bg-muted/40 text-muted-foreground hover:border-brand/40 hover:bg-brand/10 hover:text-foreground"
                  }`}
                >
                  <Bookmark
                    className={`size-3.5 ${isSaved ? "fill-amber-500 text-amber-700 dark:text-amber-400" : ""}`}
                  />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Badges: Work Mode / Seniority / Experience / Location / Salary */}
        <div className="w-full flex flex-wrap items-center gap-1.5 text-sm">
          <div className="text-xs flex items-center gap-1.5 text-foreground font-medium border-r pr-1.5 mr-0.5">
            <Building2 className="size-3.5 text-muted-foreground shrink-0" />
            <span className="truncate max-w-[140px] sm:max-w-[200px]">
              {company || "Confidential"}
            </span>
          </div>
          {workMode && (
            <Badge
              variant="secondary"
              className={`capitalize font-medium ${
                workMode === "remote"
                  ? "bg-brand/10 text-brand-foreground border-brand/20"
                  : workMode === "hybrid"
                    ? "bg-muted/10 text-muted-foreground border-border"
                    : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20"
              }`}
            >
              {workMode}
            </Badge>
          )}
          {country && (
            <Badge
              variant="outline"
              className="font-medium gap-1 text-xs"
              title={getCountryName(country)}
            >
              <span className="text-sm leading-none">{getCountryFlag(country)}</span>
              <span>{country}</span>
            </Badge>
          )}

          {locationPrimary && (
            <Badge
              variant="outline"
              className="font-normal truncate max-w-[150px] sm:max-w-[180px]"
            >
              <MapPin className="size-3 text-muted-foreground shrink-0" />
              <span className="truncate">{locationPrimary}</span>
            </Badge>
          )}

          {seniority && (
            <Badge variant="outline" className="capitalize font-normal">
              <Briefcase className="size-3 text-muted-foreground" />
              {seniority}
            </Badge>
          )}

          {!minimal && expString && (
            <Badge variant="outline" className="font-normal">
              {expString}
            </Badge>
          )}

          {compString && (
            <Badge
              variant="secondary"
              className={`font-semibold bg-emerald-500/10 text-emerald-500 border-emerald-500/20 font-mono ${
                minimal ? "" : "ml-auto"
              }`}
            >
              <DollarSign className="size-3" />
              {compString}
            </Badge>
          )}
        </div>

        {/* Fit One-Liner (Hidden in minimal mode) */}
        {!minimal && oneLiner && (
          <p className="text-sm text-muted-foreground line-clamp-1 leading-relaxed">{oneLiner}</p>
        )}

        {/* Top Required Skills Tags */}
        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
          {topSkills.slice(0, minimal ? 3 : 5).map((skill, idx) => (
            <span
              key={idx}
              className="rounded-md bg-muted/80 px-2 py-0.5 text-[11px] font-medium text-foreground/80 group-hover:bg-muted"
            >
              {skill}
            </span>
          ))}
          {topSkills.length > (minimal ? 3 : 5) && (
            <span className="text-[10px] text-muted-foreground">
              +{topSkills.length - (minimal ? 3 : 5)}
            </span>
          )}

          <span className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
            <Clock className="size-3" />
            {timeAgo}
          </span>
        </div>
      </div>
    </div>
  );
}
